import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

// Import UI components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// Import API
import { api } from "@/lib/api";

// Import types
import { MainSystemPrompt, MainSystemPromptCreate } from "@/lib/types";

const AddMainSystemPromptSchema = z.object({
  name: z.string().nonempty("Name is required"),
  prompt: z.string().nonempty("Prompt is required"),
});

type AddMainSystemPromptValues = z.infer<typeof AddMainSystemPromptSchema>;

function MainSystemPromptPage() {
  const [mainSystemPrompts, setMainSystemPrompts] = useState<
    MainSystemPrompt[]
  >([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const form = useForm<AddMainSystemPromptValues>({
    resolver: zodResolver(AddMainSystemPromptSchema),
    defaultValues: {
      name: "",
      prompt: "",
    },
  });

  useEffect(() => {
    fetchMainSystemPrompts();
  }, []);

  const fetchMainSystemPrompts = async () => {
    try {
      const prompts = await api.getAllMainSystemPrompts();
      setMainSystemPrompts(
        prompts.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
      );
    } catch (error) {
      console.error("Failed to fetch main system prompts:", error);
      toast({
        title: "Failed to load main system prompts",
        description: String(error),
        variant: "destructive",
      });
    }
  };

  const onSubmit = async (data: AddMainSystemPromptValues) => {
    try {
      const newPrompt = await api.createMainSystemPrompt(data); //Data now includes name
      setMainSystemPrompts((prev) => [newPrompt, ...prev]);
      toast({ title: "Main system prompt added successfully" });
      form.reset();
      setIsAddDialogOpen(false);
    } catch (error) {
      console.error("Failed to add main system prompt:", error);
      toast({
        title: "Failed to add main system prompt",
        description: String(error),
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="m-8">
      <CardHeader className="flex flex-row justify-between items-center mb-4">
        <CardTitle>Main System Prompts</CardTitle>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>Add Main System Prompt</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Main System Prompt</DialogTitle>
              <DialogDescription>
                Enter the new main system prompt.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...form.register("name")} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
                <FormItem>
                  <FormLabel>Prompt</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter the main system prompt..."
                      {...form.register("prompt")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
                <DialogFooter>
                  <Button type="submit">Add Prompt</Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Prompt</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead>Updated At</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mainSystemPrompts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  No main system prompts available.
                </TableCell>
              </TableRow>
            ) : (
              mainSystemPrompts.map((prompt, index) => (
                <TableRow key={prompt.id}>
                  <TableCell>{prompt.name}</TableCell>
                  <TableCell>
                    <span className="whitespace-pre-wrap">{prompt.prompt}</span>
                  </TableCell>
                  <TableCell>
                    {new Date(prompt.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {new Date(prompt.updated_at).toLocaleString()}
                  </TableCell>
                  <TableCell>{index === 0 && <Badge>Latest</Badge>}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default MainSystemPromptPage;
